import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { api, avatarSrc, formatCurrency, toImageSrc } from "@/lib/api";
import { House, User as UserIcon, Briefcase, Compass } from "@phosphor-icons/react";

// Global ⌘K / Ctrl+K palette. Searches own properties + own clients + agents.
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ properties: [], clients: [], agents: [] });
  const nav = useNavigate();

  // Toggle with ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults({ properties: [], clients: [], agents: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch (e) { /* silent */ }
    }, 180);
    return () => clearTimeout(t);
  }, [query, open]);

  const go = useCallback((to) => {
    setOpen(false);
    setQuery("");
    nav(to);
  }, [nav]);

  const total = results.properties.length + results.clients.length + results.agents.length;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search properties, clients, agents…"
        value={query}
        onValueChange={setQuery}
        data-testid="palette-input"
      />
      <CommandList data-testid="palette-list">
        {query.trim() && total === 0 && (
          <CommandEmpty>No matches. Try a city, agent name, or client phone.</CommandEmpty>
        )}
        {!query.trim() && (
          <div className="px-3 py-6 text-center text-xs text-zinc-500">
            <Compass size={20} className="mx-auto text-gold mb-2" weight="duotone" />
            Type to search across your inventory, clients, and the agent network.
            <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
              ⌘K · /
            </div>
          </div>
        )}
        {results.properties.length > 0 && (
          <CommandGroup heading="Properties" data-testid="palette-group-properties">
            {results.properties.map((p) => (
              <CommandItem
                key={p.id}
                value={`property-${p.id}-${p.title}`}
                onSelect={() => go(`/dashboard?focus=${p.id}`)}
                data-testid={`palette-property-${p.id}`}
              >
                <div className="h-8 w-8 rounded-md bg-ink-700 overflow-hidden shrink-0 flex items-center justify-center">
                  {p.image_url
                    ? <img src={toImageSrc(p.image_url)} alt="" className="h-full w-full object-cover" />
                    : <House size={16} className="text-gold" weight="duotone" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{p.title}</div>
                  <div className="truncate text-[11px] text-zinc-500">
                    {p.location} · {p.listing_for === "rent" ? "Rent" : "Sale"} · {p.status}
                  </div>
                </div>
                <div className="font-mono text-xs text-gold whitespace-nowrap">{formatCurrency(p.price)}</div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.clients.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Clients" data-testid="palette-group-clients">
              {results.clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`client-${c.id}-${c.name}`}
                  onSelect={() => go(`/dashboard?clientFocus=${c.id}`)}
                  data-testid={`palette-client-${c.id}`}
                >
                  <Briefcase size={16} className="text-gold" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{c.name}</div>
                    <div className="truncate text-[11px] text-zinc-500">
                      {c.phone || c.email || c.preferred_city || "—"}
                    </div>
                  </div>
                  {c.budget > 0 && (
                    <div className="font-mono text-xs text-gold whitespace-nowrap">{formatCurrency(c.budget)}</div>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results.agents.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Agents" data-testid="palette-group-agents">
              {results.agents.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`agent-${a.id}-${a.name}`}
                  onSelect={() => go(`/agents/${a.id}`)}
                  data-testid={`palette-agent-${a.id}`}
                >
                  <img
                    src={avatarSrc(a.avatar_url)}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                    onError={(e) => { e.target.style.visibility = "hidden"; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{a.name}</div>
                    <div className="truncate text-[11px] text-zinc-500">
                      {[a.specialty, a.area, a.city].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <UserIcon size={14} className="text-zinc-600" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
